import { Link } from 'react-router-dom';
import { ArrowLeft, Instagram, Link2, Mail, MapPin, QrCode } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { useShopPath } from '@/hooks/useShopPath';

/**
 * "Unified Commerce for Independents" explainer (NEA-5935) — a 1:1 port of the
 * fresh-dashboard `LearnUnifiedCommerce` design, reached from the Local Potential
 * page's "Learn about Unified Commerce" link. Static content; the back link is
 * shop-scoped via useShopPath so it stays on the right location in a chain.
 */
const WINS = [
	{
		icon: Instagram,
		title: 'Instagram & TikTok bio link',
		body: 'Replace your bio link with your Unify storefront so every profile visit can browse your live local stock.'
	},
	{
		icon: Link2,
		title: 'Linktree → storefront',
		body: 'Make your storefront the top link in your link-in-bio tools — above any other shop or page.'
	},
	{
		icon: Mail,
		title: "A 'shop local stock' email button",
		body: "Add one button to your monthly newsletter pointing customers to what's in store today."
	},
	{
		icon: MapPin,
		title: 'Google Business Profile link',
		body: 'Set your storefront as the website on your Google Business Profile so search & Maps users land there.'
	},
	{
		icon: QrCode,
		title: 'In-store QR to your storefront',
		body: 'Print a small QR at the till — shoppers scan to keep browsing your stock from home.'
	}
];

export function LearnUnifiedCommerce() {
	const { shopPath } = useShopPath();

	return (
		<div className="max-w-3xl space-y-10">
			<div className="inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
				<Link
					to={shopPath('/local-potential')}
					aria-label="Back"
					className="inline-flex cursor-pointer items-center gap-1 transition-colors hover:text-foreground"
				>
					<ArrowLeft className="h-3 w-3" />
					<span>Back</span>
				</Link>
			</div>

			<header>
				<p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Learn</p>
				<h1 className="mt-1 text-3xl font-extrabold tracking-tight md:text-[34px]">
					Unified Commerce <span className="text-gradient-unify">for Independents</span>
				</h1>
				<p className="mt-2 text-base text-muted-foreground">
					The big retailers win because every channel they own — socials, email, website, in-store — points back to the same live
					stock. With Unify, you can do the same.
				</p>
			</header>

			<section>
				<h2 className="text-xl font-bold text-foreground">What is Unified Commerce?</h2>
				<p className="mt-2 text-base leading-relaxed text-muted-foreground">
					Unified Commerce means your customer sees the same products, the same prices and the same availability wherever they
					meet you — on Instagram, in your email, on Google, or in your shop. Unify connects the channels we manage for you. The
					other half is the channels <span className="font-semibold text-foreground">you</span> already own.
				</p>
			</section>

			<section>
				<h2 className="text-xl font-bold text-foreground">Why your own channels matter most</h2>
				<p className="mt-2 text-base leading-relaxed text-muted-foreground">
					Your followers, your email list and your website visitors are already interested. Sending them to your live storefront
					is the highest-converting traffic you can get — and it costs nothing.
				</p>
			</section>

			<section>
				<h2 className="text-xl font-bold text-foreground">5 quick wins</h2>
				<div className="mt-3 space-y-3">
					{WINS.map((win) => {
						const Icon = win.icon;
						return (
							<SoftCard key={win.title} className="!p-5">
								<div className="flex items-start gap-4">
									<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-action-soft text-action-soft-foreground">
										<Icon className="h-5 w-5" />
									</div>
									<div className="min-w-0 flex-1">
										<h3 className="text-[15px] font-bold text-foreground">{win.title}</h3>
										<p className="mt-0.5 text-sm text-muted-foreground">{win.body}</p>
									</div>
								</div>
							</SoftCard>
						);
					})}
				</div>
			</section>
		</div>
	);
}
